import { useState, useRef, useEffect } from 'react';
import GenerateButton from './GenerateButton';

export default function RefineForm({
    activeLogo,
    onBack,
    onRefine,
    transparentBackground,
    onTransparentBackgroundChange,
    isLoading,
}) {
    const [refinementPrompt, setRefinementPrompt] = useState('');
    const [colorHints, setColorHints] = useState([]);
    const [hexDrafts, setHexDrafts] = useState([]);
    const textareaRef = useRef(null);

    useEffect(() => {
        setTimeout(() => textareaRef.current?.focus(), 100);
    }, []);

    const normalizedColorHints = Array.isArray(colorHints) ? colorHints : [];
    const hasAnyColorHint = normalizedColorHints.length > 0;

    const addColorHint = () => {
        setColorHints((prev) => [...prev, { color: '#000000', purpose: '' }].slice(0, 3));
        setHexDrafts((prev) => [...prev, '#000000'].slice(0, 3));
    };

    const removeColorHint = (idx) => {
        setColorHints((prev) => prev.filter((_, i) => i !== idx));
        setHexDrafts((prev) => prev.filter((_, i) => i !== idx));
    };

    const updateColorHint = (idx, patch) => {
        setColorHints((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], ...patch };
            return next;
        });
    };

    const setHexDraft = (idx, value) => {
        const nextDrafts = [...hexDrafts];
        nextDrafts[idx] = value;
        setHexDrafts(nextDrafts);

        const normalized = (value || '').trim().toUpperCase();
        if (/^#([0-9A-F]{6})$/.test(normalized)) {
            updateColorHint(idx, { color: normalized });
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (isLoading) return;

        const promptText = refinementPrompt.trim();
        const sanitizedHints = normalizedColorHints
            .filter((h) => h?.color && /^#([0-9A-Fa-f]{6})$/.test(h.color))
            .map((h) => ({
                color: String(h.color).toUpperCase(),
                purpose: (h.purpose || '').trim(),
            }));

        const hintSuffix = sanitizedHints.length
            ? `\n\nColor hints: ${sanitizedHints
                .map((h) => (h.purpose ? `${h.color} (${h.purpose})` : h.color))
                .join(', ')}`
            : '';

        if (!promptText && !sanitizedHints.length) return;

        onRefine(activeLogo, `${promptText}${hintSuffix}`.trim(), {
            transparentBackground,
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.metaKey && refinementPrompt.trim()) {
            handleSubmit(e);
        }
    };

    return (
        <div className="glass flex flex-col h-full overflow-hidden shadow-2xl shadow-violet-500/5">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-white/5 bg-white/50 dark:bg-black/20">
                <button
                    onClick={onBack}
                    className="group flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-violet-500 transition-colors"
                >
                    <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Results
                </button>
                <div className="px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-600 dark:text-violet-400 text-[9px] font-bold uppercase tracking-widest">
                    Refine Icon
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 custom-scrollbar space-y-8 animate-fade-in-up">
                {/* Source Preview */}
                <div className="space-y-4">
                    <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Source Icon</p>
                    <div className="relative group aspect-square max-w-[120px] mx-auto rounded-2xl overflow-hidden border border-slate-200 dark:border-white/10 shadow-2xl">
                        <img src={activeLogo.url} alt="Base" className="w-full h-full object-contain bg-white dark:bg-black/20" />
                        <div className="absolute inset-0 bg-violet-600/10 mix-blend-overlay" />
                    </div>
                </div>

                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <label htmlFor="refine-prompt" className="text-sm font-bold text-slate-700 dark:text-slate-200">
                            Refinement Prompt
                        </label>
                        <span className="text-xs font-bold text-slate-400 dark:text-slate-500">
                            {refinementPrompt.length}/250
                        </span>
                    </div>
                    <textarea
                        ref={textareaRef}
                        id="refine-prompt"
                        value={refinementPrompt}
                        onChange={(e) => setRefinementPrompt(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="e.g. Add a santa hat, make it simpler..."
                        rows={4}
                        maxLength={250}
                        className="textarea focus:ring-violet-500/50 bg-white dark:bg-black/20 border-slate-200 dark:border-white/5 text-slate-900 dark:text-white text-sm leading-relaxed placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    />
                </div>

                {/* Options */}
                <div className="space-y-6 pt-4 border-t border-slate-200 dark:border-white/5">
                    <div className="flex items-center justify-between gap-4 bg-white/30 dark:bg-white/[0.02] p-4 rounded-2xl border border-slate-200 dark:border-white/10">
                        <div className="min-w-0">
                            <p className="text-sm font-bold text-slate-800 dark:text-white mb-0.5">Transparent background</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Best for app icons and stickers</p>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            onClick={() => onTransparentBackgroundChange?.(!transparentBackground)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ${transparentBackground ? 'bg-violet-600 shadow-[0_0_15px_rgba(139,92,246,0.3)]' : 'bg-slate-200 dark:bg-white/10'
                                }`}
                        >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${transparentBackground ? 'translate-x-6' : 'translate-x-1'
                                }`} />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Color hints</p>
                            <button
                                type="button"
                                onClick={addColorHint}
                                disabled={normalizedColorHints.length >= 3}
                                className="text-xs font-bold text-violet-400 hover:text-violet-300 transition-colors disabled:opacity-50"
                            >
                                + Add Color
                            </button>
                        </div>

                        <div className="grid grid-cols-1 gap-2.5">
                            {normalizedColorHints.map((hint, idx) => (
                                <div key={idx} className="flex items-center gap-3 bg-white/40 dark:bg-white/[0.02] rounded-2xl p-3 border border-slate-200 dark:border-white/10 group transition-all hover:bg-white/60 dark:hover:bg-white/[0.05]">
                                    <div className="relative h-9 w-9 rounded-xl border-2 border-white/50 dark:border-white/10 overflow-hidden shrink-0 shadow-lg" style={{ backgroundColor: hint?.color }}>
                                        <input
                                            type="color"
                                            value={hint?.color || '#000000'}
                                            onChange={(e) => {
                                                const next = e.target.value.toUpperCase();
                                                updateColorHint(idx, { color: next });
                                                setHexDraft(idx, next);
                                            }}
                                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                        />
                                    </div>
                                    <input
                                        type="text"
                                        value={hint?.purpose || ''}
                                        onChange={(e) => updateColorHint(idx, { purpose: e.target.value })}
                                        placeholder="Color role (e.g. Neon Accents)"
                                        className="bg-transparent text-xs font-bold text-slate-700 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 border-none focus:ring-0 p-0 flex-1"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeColorHint(idx)}
                                        className="text-slate-300 dark:text-slate-700 hover:text-red-500 transition-colors p-1"
                                    >
                                        <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="pt-4">
                    <GenerateButton
                        onClick={handleSubmit}
                        isLoading={isLoading}
                        disabled={!refinementPrompt.trim() && !hasAnyColorHint}
                    />
                    <p className="mt-4 text-center text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                        Generation takes ~15 seconds
                    </p>
                </div>
            </div>
        </div>
    );
}
